import useCurrentMerchantUser from 'hooks/useCurrentMerchantUser'
import useFlag from 'hooks/useFlag'
import { useIsRbacAdminWithLoading } from 'hooks/useIsRbacAdmin'
import { useSelector } from 'react-redux'
import { selectLegalDocsEnforced } from 'slices/legalDocsBlock'

/**
 * Returns whether the current user is an admin, with a loading indicator for
 * callers (e.g. route guards) that need to distinguish "not admin" from
 * "still resolving RBAC data." Legacy is_admin reads are synchronous, so
 * legacy mode never reports isLoading=true. The legal-docs-block fallback
 * still applies in both modes — see useIsAdmin docs below.
 */
export const useIsAdminWithLoading = (): {
  isAdmin: boolean
  isLoading: boolean
} => {
  const rbacEnabled = useFlag('role-based-access-control', false)
  const { isRbacAdmin, isLoading } = useIsRbacAdminWithLoading()
  const user = useCurrentMerchantUser()
  const legalDocsEnforced = useSelector(selectLegalDocsEnforced)
  const isLegacyAdmin = user?.is_admin || false
  const baseAdmin = rbacEnabled ? isRbacAdmin : isLegacyAdmin
  // isLoading is only true when we're *actually* waiting on async RBAC data.
  // Legacy mode short-circuits because is_admin is read synchronously off the
  // merchant_user record. Legal-docs enforcement short-circuits because a
  // legal_docs_required 403 is itself proof of admin status (the backend
  // only sends those to admins), so we render immediately instead of
  // flash-redirecting while the RBAC query is still in flight.
  return {
    isAdmin: baseAdmin || legalDocsEnforced,
    isLoading: rbacEnabled && isLoading && !legalDocsEnforced,
  }
}

/**
 * Returns true if the current user is an admin.
 * When RBAC flag is on: checks RBAC admin role assignment.
 * When RBAC flag is off: checks legacy is_admin field.
 *
 * Falls back to the legal-docs-block enforcement signal when set, since the
 * backend only returns `legal_docs_required` 403s to admins — so receiving
 * one is itself proof of admin status, even before merchant_user data loads.
 */
const useIsAdmin = (): boolean => useIsAdminWithLoading().isAdmin

export default useIsAdmin
